import { useEffect, useState } from "react";
import { notification } from "antd";
import { usePersonSummaryCtx } from "../context/PersonSummaryContext";
import { useServiceUserCreationCtx } from "../context/ServiceUserCreationContext";
import {
  PersonInitialValues,
  WandaUserInfo,
  defaultProfileInitialValues,
} from "../types/PersonTypes";
import {
  getImageUrl,
  getInitialAddress,
  getInitialDateOfBirth,
  getInitialWandaAccommodations,
} from "../helpers/personHelpers";
import { PersonAttributesKey } from "../types/ApplicationTypes";
import { getPersonAttributeKey } from "../services/RegistrationService";
import { LookupListItems } from "../types/SubscriptionTypes";
import { useTripsCtx } from "../context/TripDetailsContext";

export function useProfileForm(personId: number) {
  const [initialValues, setInitialValues] = useState<PersonInitialValues>(
    defaultProfileInitialValues
  );
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(true);

  const { serviceDataCtx } = useTripsCtx();
  const [serviceData] = serviceDataCtx;

  const { caregiverSummaryInfo, isLoadingPersonData } = usePersonSummaryCtx();

  const {
    getRelationshipList,
    relationshipTypesCtx,
  } = useServiceUserCreationCtx();
  const [relationshipTypes] = relationshipTypesCtx;

  useEffect(() => {
    !isLoadingPersonData && fetchEditProfileInfo();
  }, [isLoadingPersonData]);

  async function fetchEditProfileInfo() {
    try {
      setIsLoadingProfile(true);
      const isCaregiverProfile = caregiverSummaryInfo?.personId === personId;
      const selectedServiceUser = serviceData.find(
        (serviceUser: WandaUserInfo) => serviceUser?.personId === personId
      );

      let relationshipList;
      if (!relationshipTypes && !isCaregiverProfile)
        relationshipList = await getRelationshipList();
      else relationshipList = relationshipTypes;

      let userInfo;

      if (isCaregiverProfile) {
        const {
          firstName,
          lastName,
          dateOfBirth,
          primaryPhone,
          wandaEmail,
          imageUrl,
          homeAddress,
          mailingAddress,
        } = caregiverSummaryInfo;

        const initialHomeAddress = getInitialAddress(homeAddress);
        const initialMailingAddress = getInitialAddress(mailingAddress);
        const personAttributesList = await getPersonAttributeKey({
          personId: caregiverSummaryInfo.personId,
        });
        const wandaAccommodationsObj = personAttributesList?.find(
          (attribute: { keyTypeId: number }) =>
            attribute?.keyTypeId === PersonAttributesKey.WandaAccommodations
        );

        userInfo = {
          firstName,
          lastName,
          phone: primaryPhone,
          email: wandaEmail,
          dateOfBirth: getInitialDateOfBirth(dateOfBirth),
          isSameAsHomeAddress:
            JSON.stringify(initialHomeAddress) ===
            JSON.stringify(initialMailingAddress),
          homeAddress: initialHomeAddress,
          mailingAddress: initialMailingAddress,
          photo: imageUrl,
          wandaAccommodations: getInitialWandaAccommodations(
            wandaAccommodationsObj?.keyAttributes
          ),
        };
      } else {
        const {
          firstName,
          lastName,
          dateOfBirth,
          imageKey,
          personAttributes,
          wandaAccommodations,
          profileImageUrl,
        } = selectedServiceUser;
        const { homeAddress, mailingAddress, personContact } = personAttributes;
        const { primaryPhone, wandaEmail, wandaRelation } = personContact;

        const relationship = relationshipList.find(
          (relation: LookupListItems) => relation.label === wandaRelation
        )?.value;

        let imageUrl;
        if (profileImageUrl) imageUrl = profileImageUrl;
        else if (imageKey) imageUrl = await getImageUrl(imageKey);

        userInfo = {
          firstName,
          lastName,
          phone: primaryPhone,
          email: wandaEmail,
          relationship,
          dateOfBirth: getInitialDateOfBirth(dateOfBirth),
          isSameAsHomeAddress:
            JSON.stringify(homeAddress) === JSON.stringify(mailingAddress),
          homeAddress: getInitialAddress(homeAddress),
          mailingAddress: getInitialAddress(mailingAddress),
          photo: imageUrl,
          wandaAccommodations,
        };
      }

      setInitialValues(userInfo);
    } catch (error) {
      notification.error({
        message: "Failed to Fetch User Profile Details",
      });
    } finally {
      setIsLoadingProfile(false);
    }
  }

  return {
    initialValues,
    isLoadingProfile,
  };
}
